import { Assignment } from "../../../data-types/Types";

type DateTimeParts = {
  date: string;
  time: string;
};

type Availability = {
  isOpen: boolean;
  isFuture: boolean;
  isPastDue: boolean;
};

const pad2 = (value: number) => value.toString().padStart(2, "0");

const toLocalDateString = (utcMs: number) => {
  const date = new Date(utcMs);
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

const toLocalTimeString = (utcMs: number) => {
  const date = new Date(utcMs);
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

const inferHasTime = (utcMs: number, mode: "start" | "end") => {
  const date = new Date(utcMs);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  if (mode === "start") {
    return !(hours === 0 && minutes === 0);
  }
  return !(hours === 23 && minutes === 59);
};

export const getLocalDateTimeParts = (
  utcMs: number | undefined,
  hasTime: boolean | undefined,
  mode: "start" | "end"
): DateTimeParts => {
  if (!utcMs) {
    return { date: "", time: "" };
  }
  const resolvedHasTime = hasTime ?? inferHasTime(utcMs, mode);
  return {
    date: toLocalDateString(utcMs),
    time: resolvedHasTime ? toLocalTimeString(utcMs) : "",
  };
};

export const buildUtcMsFromLocalParts = (
  dateValue: string,
  timeValue: string,
  mode: "start" | "end"
): { utcMs?: number; hasTime?: boolean } => {
  if (!dateValue) {
    return {};
  }
  const [year, month, day] = dateValue.split("-").map((value) => Number.parseInt(value, 10));
  if (!year || !month || !day) {
    return {};
  }
  let hours = 0;
  let minutes = 0;
  let hasTime = false;
  if (timeValue) {
    const [parsedHours, parsedMinutes] = timeValue.split(":").map((value) => Number.parseInt(value, 10));
    hours = Number.isFinite(parsedHours) ? parsedHours : 0;
    minutes = Number.isFinite(parsedMinutes) ? parsedMinutes : 0;
    hasTime = true;
  } else if (mode === "end") {
    hours = 23;
    minutes = 59;
  }
  const localDate = new Date(year, month - 1, day, hours, minutes, 0, 0);
  return { utcMs: localDate.getTime(), hasTime };
};

export const getAssignmentAvailability = (assignment: Assignment, nowUtcMs = Date.now()): Availability => {
  const startAtUtcMs = assignment.startAtUtcMs;
  const endAtUtcMs = assignment.endAtUtcMs;
  const isFuture = startAtUtcMs ? nowUtcMs < startAtUtcMs : false;
  const isPastDue = endAtUtcMs ? nowUtcMs > endAtUtcMs : false;
  const isOpen = !isFuture && !isPastDue;
  return { isOpen, isFuture, isPastDue };
};

export const formatLocalDateTime = (
  utcMs: number,
  hasTime: boolean | undefined,
  mode: "start" | "end"
) => {
  const parts = getLocalDateTimeParts(utcMs, hasTime, mode);
  if (!parts.date) {
    return "";
  }
  return parts.time ? `${parts.date} ${parts.time}` : parts.date;
};

export const formatAssignmentWindow = (assignment: Assignment, nowUtcMs = Date.now()) => {
  const { startAtUtcMs, endAtUtcMs, startAtHasTime, endAtHasTime } = assignment;
  const startText = startAtUtcMs
    ? formatLocalDateTime(startAtUtcMs, startAtHasTime, "start")
    : "";
  const endText = endAtUtcMs
    ? formatLocalDateTime(endAtUtcMs, endAtHasTime, "end")
    : "";
  const startPast = startAtUtcMs ? nowUtcMs >= startAtUtcMs : false;
  const endPast = endAtUtcMs ? nowUtcMs >= endAtUtcMs : false;

  if (startText && endText) {
    if (endPast) {
      return `Opened ${startText} – closed ${endText}`;
    }
    if (startPast) {
      return `Opened ${startText} – closes ${endText}`;
    }
    return `Opens ${startText} – closes ${endText}`;
  }
  if (startText) {
    return startPast ? `Opened ${startText}` : `Opens ${startText}`;
  }
  if (endText) {
    return endPast ? `Closed ${endText}` : `Closes ${endText}`;
  }
  return "Always open";
};
